import React, {ComponentProps, ReactElement, useMemo, useState} from 'react';

import {PropertyId} from '../../../query/graphql';
import {useSmallScaleCalculator} from '../../decision/smallScaleCalculator';
import {SIMPLE_CALCULATOR_CROPS_NAMES, SimpleCalculatorCrop} from '../../decision/types';
import {PROPERTIES} from '../../resources/properties';
import Select from '../Select';
import SimpleCalculatorCard from './SimpleCalculatorCard';
import SimpleCalculatorTable from './SimpleCalculatorTable';
import ValueWithUnit from './ValueWithUnit';

type Props = {};

const propertyOptions = Object.entries(PROPERTIES).map(([propertyId, property]) => ({
  label: property.shortDesc,
  value: propertyId,
}));

const cropOptions = Object.entries(SIMPLE_CALCULATOR_CROPS_NAMES).map(([cropId, name]) => ({
  label: name,
  value: cropId,
}));

type Row = ComponentProps<typeof SimpleCalculatorTable>['values'][number][number];

const SmallScaleCalculator = ({}: Props): ReactElement => {
  const [propertyId, setPropertyId] = useState<PropertyId>();
  const [cropId, setCropId] = useState<SimpleCalculatorCrop>();

  const input = {propertyId, cropId: cropId as SimpleCalculatorCrop | undefined};
  const propertyName = propertyId ? PROPERTIES[propertyId].shortDesc : 'Select';
  const cropName = cropId ? SIMPLE_CALCULATOR_CROPS_NAMES[cropId] : 'Select';

  const {data} = useSmallScaleCalculator(input);

  const values = useMemo<Row[][]>(
    () => [
      [
        {content: 'Property type', borderL: 'top'},
        {
          type: 'input',
          content: (
            <Select
              floating
              variant={'table'}
              buttonContent={propertyName}
              options={propertyOptions}
              selectedValues={propertyId ? [propertyId] : []}
              onOptionSelected={value => setPropertyId(value as PropertyId)}
              buttonClassName={'rounded-tr-2xl'}
            />
          ),
          borderR: 'top',
        },
      ],
      [
        {content: 'Annual consumption', borderL: 'bottom'},
        {content: <ValueWithUnit value={data?.annualConsumption} unit={'kWh'} />, borderR: 'bottom'},
      ],
      [
        {content: 'Crop choice', borderL: 'top'},
        {
          type: 'input',
          content: (
            <Select
              floating
              variant={'table'}
              buttonContent={cropName}
              options={cropOptions}
              selectedValues={cropId ? [cropId] : []}
              onOptionSelected={value => setCropId(value as SimpleCalculatorCrop)}
              buttonClassName={'rounded-tr-2xl'}
            />
          ),
          borderR: 'top',
        },
      ],
      [
        {content: 'Moisture content of feedstock when used', borderL: 'middle'},
        {content: <ValueWithUnit value={data?.inUseMC} unit={'%'} />, borderR: 'middle'},
      ],
      [
        {content: 'Generic yield', borderL: 'middle'},
        {content: <ValueWithUnit value={data?.genericYield} unit={'tonnes/ha'} />, borderR: 'middle'},
      ],
      [
        {content: 'CV of material', borderL: 'middle'},
        {content: <ValueWithUnit value={data?.cv} unit={'kWh/tonne'} />, borderR: 'middle'},
      ],
      [
        {content: 'Years to self sufficiency', borderL: 'middle'},
        {content: <ValueWithUnit value={data?.yearsToSelfSufficiency} unit={'years'} />, borderR: 'middle'},
      ],
      [
        {content: 'Bulk density', borderL: 'bottom'},
        {content: <ValueWithUnit value={data?.bulkDensity} unit={'tonnes/m3'} />, borderR: 'bottom'},
      ],
      [{content: 'Annual requirement'}, {content: <ValueWithUnit value={data?.annualRequirement} unit={'tonnes'} />}],
      [{content: 'Area required'}, {content: <ValueWithUnit value={data?.areaRequired} unit={'ha'} />}],
      [{content: 'Storage space'}, {content: <ValueWithUnit value={data?.storageSpace} unit={'m3'} />}],
    ],
    [
      cropId,
      cropName,
      propertyId,
      propertyName,
      data?.annualConsumption,
      data?.annualRequirement,
      data?.areaRequired,
      data?.bulkDensity,
      data?.cv,
      data?.genericYield,
      data?.inUseMC,
      data?.storageSpace,
      data?.yearsToSelfSufficiency,
    ]
  );

  return (
    <SimpleCalculatorCard title={'Small to Medium Scale Use'}>
      <SimpleCalculatorTable values={values} />
    </SimpleCalculatorCard>
  );
};

export default SmallScaleCalculator;
